import { DropIcon } from "@components/Icons";
import { useTheme } from "@hooks/useTheme";
import { useFetchData } from "@hooks/useFetchData";
import { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { CustomDatePicker } from "@components/Forms";
import AddVerificationFileModal from "./AddVerificationFileModal";
import { ComponentTable } from "../Tables";
import queryKeys from "@configs/react-query/query-key";
import UserNameWithId from "@components/UI/UserNameWithId";

const AddVerificationReportForm = forwardRef(({
  initVerificationFile = null,
  dataTestId = "add-verification-report-form",
}, ref) => {
  const fieldContainerTestId = (name) => `${dataTestId}-field-${name}-container`;
  const fieldLabelTestId = (name) => `${dataTestId}-field-${name}-label`;
  const fieldInputTestId = (name) => `${dataTestId}-field-${name}-input`;
  const { colors } = useTheme();
  const { t } = useTranslation();

  const [formData, setFormData] = useState({
    verificationReportName: "",
    noticeInfoCompletionDate: "",
    verificationFile: initVerificationFile || null,
    scanComponentData: [],
  });

  const [showAddFileModal, setShowAddFileModal] = useState(false);

  useEffect(() => {
    if (initVerificationFile) {
      setFormData(prev => ({
        ...prev,
        verificationFile: initVerificationFile
      }));
    }
  }, [initVerificationFile]);

  /** clarity | fossid | clarity_air — from verificationType, else clarityFlag */
  const reportVerificationKind = useMemo(() => {
    const vf = formData.verificationFile;
    if (!vf) return null;
    const vt = vf.verificationType;
    if (vt === "clarity") return "clarity";
    if (vt === "fossid") return "fossid";
    if (vt === "clarity_air" || vt === "clarityair") return "clarity_air";
    return vf.clarityFlag === "Y" ? "clarity" : "fossid";
  }, [formData.verificationFile]);

  // Project details
  const { data: projectData } = useFetchData(
    [queryKeys.getProjectByProjectId, formData.verificationFile?.projectSeq],
    `/project/${formData.verificationFile?.projectSeq}`,
    { method: "GET" },
    {},
    {
      enabled: !!formData.verificationFile?.projectSeq,
      refetchOnMount: true,
      staleTime: 0,
    }
  );

  // Binary Scan components
  const { data: binaryScanComponentResponse } = useFetchData(
    [queryKeys.getBinaryScanComponentByProjectSeqAndVerificationVersion, formData.verificationFile?.projectId, formData.verificationFile?.verificationVersion],
    "/binary_scan_component",
    {
      method: "GET",
      params: {
        projectId: formData.verificationFile?.projectId,
        verificationVersion: formData.verificationFile?.verificationVersion,
      },
    },
    {},
    {
      enabled:
        !!formData.verificationFile?.projectId &&
        !!formData.verificationFile?.verificationVersion &&
        reportVerificationKind === "clarity",
    }
  );

  // Source code scan components
  const { data: sourceCodeScanComponentResponse } = useFetchData(
    [queryKeys.getSourceCodeScanComponentByProjectIdAndVerificationVersion, formData.verificationFile?.projectId, formData.verificationFile?.verificationVersion],
    "/source_code_scan_component",
    {
      method: "GET",
      params: {
        projectId: formData.verificationFile?.projectId,
        verificationVersion: formData.verificationFile?.verificationVersion,
      },
    },
    {},
    {
      enabled:
        !!formData.verificationFile?.projectId &&
        !!formData.verificationFile?.verificationVersion &&
        reportVerificationKind === "fossid",
    }
  );

  // Clarity AIR scan components (same row shape as source / SourceCodeScanComponent)
  const { data: clarityAirScanComponentResponse } = useFetchData(
    [
      queryKeys.getClarityAirScanComponentBySeq,
      formData.verificationFile?.projectId,
      formData.verificationFile?.verificationVersion,
    ],
    `/${queryKeys.getClarityAirScanComponentBySeq}`,
    {
      method: "GET",
      params: {
        projectId: formData.verificationFile?.projectId,
        verificationVersion: formData.verificationFile?.verificationVersion,
      },
    },
    {},
    {
      enabled:
        !!formData.verificationFile?.projectId &&
        !!formData.verificationFile?.verificationVersion &&
        reportVerificationKind === "clarity_air",
    }
  );

  // License policy
  const { data: licensePolicyResponse } = useFetchData(
    [queryKeys.getLicensePolicyByLicensePolicySeq, projectData?.licensePolicySeq],
    `/license_policy/${projectData?.licensePolicySeq}`,
    { method: "GET" },
    {},
    {
      enabled: !!projectData?.licensePolicySeq,
      refetchOnMount: true,
      staleTime: 0,
    }
  );

  // Expose form data to parent
  useImperativeHandle(ref, () => ({
    getFormData: () => formData,
  }));

  // Handle manual input changes
  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleAddVerificationFile = () => {
    setShowAddFileModal(true);
  };

  const handleFileModalAdd = (file) => {
    setFormData((prev) => ({
      ...prev,
      verificationFile: file,
    }));
    setShowAddFileModal(false);
  };

  const handleFileModalClose = () => {
    setShowAddFileModal(false);
  };

  const { scanComponentData, scanComponentKey } = useMemo(() => {
    if (reportVerificationKind === "clarity") {
      return {
        scanComponentData: binaryScanComponentResponse || [],
        scanComponentKey: "binaryScanComponentSeq",
      };
    }
    if (reportVerificationKind === "clarity_air") {
      return {
        scanComponentData: clarityAirScanComponentResponse || [],
        scanComponentKey: "sourceCodeScanComponentSeq",
      };
    }
    return {
      scanComponentData: sourceCodeScanComponentResponse || [],
      scanComponentKey: "sourceCodeScanComponentSeq",
    };
  }, [
    reportVerificationKind,
    binaryScanComponentResponse,
    sourceCodeScanComponentResponse,
    clarityAirScanComponentResponse,
  ]);

  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      scanComponentData,
    }));
  }, [scanComponentData]);
  
  const licensePolicyData = useMemo(() => {
    return licensePolicyResponse;
  }, [licensePolicyResponse]);

  const vexContext = useMemo(() => {
    const vf = formData.verificationFile;
    if (!vf || !reportVerificationKind) return null;
    const scanToolCdMap = {
      clarity: "clarity",
      fossid: "fossid",
      clarity_air: "clarityair",
    };
    return {
      scanToolCd: scanToolCdMap[reportVerificationKind] || "fossid",
      verificationSeq: vf.verificationSeq,
      projectSeq: vf.projectSeq,
      projectManagerId: projectData?.projectManagerId,
      verificationStatusCd: vf.verificationStatusCd,
    };
  }, [formData.verificationFile, reportVerificationKind, projectData?.projectManagerId]);

  // Status styles (text color)
  const statusStyles = {
    ON_ANALYSIS: colors.textStatusAnalysis,
    ANALYSIS_CHECK: colors.textStatusAnalysis,
    REQUEST: colors.textStatusRequest,
    ON_VERIFICATION: colors.textStatusVerification,
    ON_REVIEW: colors.textStatusReview,
    STANDBY_FOLLOWUP: colors.textStatusFollowup,
    FOLLOWUP_CHECK: colors.textStatusFollowup,
    DONE: colors.textStatusSuccess,
    REVERIFICATION: colors.textStatusReverification,
    ERROR: colors.textStatusAnalysis,
  };

  // Status color dots (background color)
  const statusColorDot = {
    ON_ANALYSIS: colors.bgStatusAnalysis,
    ANALYSIS_CHECK: colors.bgStatusAnalysis,
    REQUEST: colors.bgStatusRequest,
    ON_VERIFICATION: colors.bgStatusVerification,
    ON_REVIEW: colors.bgStatusReview,
    STANDBY_FOLLOWUP: colors.bgStatusFollowup,
    FOLLOWUP_CHECK: colors.bgStatusFollowup,
    DONE: colors.bgStatusSuccess,
    REVERIFICATION: colors.bgStatusReverification,
    ERROR: colors.bgStatusAnalysis,
  };

  // Status text translations
  const statusText = {
    ON_ANALYSIS: t("status.analyzing"),
    ANALYSIS_CHECK: t("status.analysisCheck"),
    REQUEST: t("status.request"),
    ON_VERIFICATION: t("status.onVerification"),
    ON_REVIEW: t("status.onReview"),
    STANDBY_FOLLOWUP: t("status.standbyFollowup"),
    FOLLOWUP_CHECK: t("status.followupCheck"),
    DONE: t("status.done"),
    REVERIFICATION: t("status.reverification"),
    ERROR: t("status.error"),
  };

  const renderVerificationFileTable = () => {
    if (!formData.verificationFile) return null;

    const vf = formData.verificationFile;
    const statusCode = vf.verificationStatusCd;
    const statusName = vf.verificationStatusCdName;
    const vt = vf.verificationType;
    let toolName = t("common.fossid");
    if (vt === "clarity") {
      toolName = t("common.clarity");
    } else if (vt === "fossid") {
      toolName = t("common.fossid");
    } else if (vt === "clarity_air" || vt === "clarityair") {
      toolName = t("common.clarityair");
    } else if (vf.clarityFlag === "Y") {
      toolName = t("common.clarity");
    }

    if (!statusCode) {
      return <span className={colors.textMuted}>{toolName}</span>;
    }

    // For ON_VERIFICATION status, use the specific status name if available
    let displayText = statusText[statusCode];
    if (statusCode === "ON_VERIFICATION" && statusName) {
      // Map the status name to translation keys
      const statusNameMap = {
        "Usage Type Review": t("status.usageTypeReview"),
        "Usage Type Check": t("status.usageTypeCheck"),
        "Usage Type Input": t("status.usageTypeInput"),
      };
      
      displayText = statusNameMap[statusName] || statusName;
    }

    return (
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr>
              <th className={`text-left py-2 px-4 ${colors.textSecondary} text-sm font-medium`}>{t("project.verification-report.version")}</th>
              <th className={`text-left py-2 px-4 ${colors.textSecondary} text-sm font-medium`}>{t("project.verification.verification-file-name")}</th>
              <th className={`text-left py-2 px-4 ${colors.textSecondary} text-sm font-medium`}>{t("project.verification.verification-status")}</th>
              <th className={`text-left py-2 px-4 ${colors.textSecondary} text-sm font-medium`}>{t("common.requester")}</th>
              <th className={`text-left py-2 px-4 ${colors.textSecondary} text-sm font-medium`}>{t("project.project-name")}</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className={`py-1 px-4 ${colors.textPrimary} text-sm`}>{vf.verificationVersion}</td>
              <td className={`py-1 px-4 ${colors.textPrimary} text-sm`}>{vf.realFileName}</td>
              <td className={`py-1 px-4 ${colors.textPrimary} text-sm`}>
                <div className="flex items-center">
                  <span className={`${colors.textPrimary} mr-2`}>{toolName}</span>
                  <div className={`w-2 h-2 ${statusColorDot[statusCode]} rounded-full mr-2`}></div>
                  <span className={`${statusStyles[statusCode]} text-sm`}>{displayText || "---"}</span>
                </div>
              </td>
              <td className={`py-1 px-4 ${colors.textPrimary} text-sm`}><UserNameWithId name={vf.createrName} id={vf.createrId} /></td>
              <td className={`py-1 px-4 ${colors.textPrimary} text-sm max-w-[10rem] truncate`}>{vf.projectName}</td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div>
      <div className={`p-6 ${colors.bgPrimary} rounded-2xl`}>
        <div className={`mx-auto max-w-[85rem]`}>
          {/* Section: Verification Report Info */}
          <div className="mb-6">
            <h2 className={`${colors.textPrimary} text-xl font-semibold uppercase`}>{t("project.verification-report.verification-report-information")}</h2>
            <p className={`mb-2 ${colors.textSubTitle} text-sm font-normal`}>
              {t("project.required-fields-note")} <span className={colors.textDanger}>*</span>
            </p>
            <div className="grid grid-cols-2 gap-6">
              <div data-testid={fieldContainerTestId("verification-report-name")}>
                <label className={`mb-2 block text-sm font-semibold ${colors.textSecondary}`} data-testid={fieldLabelTestId("verification-report-name")}>
                  {t("project.verification-report.verification-report-name"	)} <span className={colors.textDanger}>*</span>
                </label>
                <input
                  type="text"
                  placeholder={t("project.verification-report.verification-report-name-placeholder")}
                  value={formData.verificationReportName}
                  onChange={(e) => handleInputChange("verificationReportName", e.target.value)}
                  className={`h-10 w-full rounded-lg border px-3 py-2 text-sm font-medium focus:outline-none ${colors.borderPrimary} ${colors.bgInput} ${colors.textPrimary} ${colors.textPlaceholder} ${colors.borderHoverPrimary} transition-colors duration-300`}
                  maxLength={100}
                  data-testid={fieldInputTestId("verification-report-name")}
                />
              </div>

              <div className="relative" data-testid={fieldContainerTestId("notice-info-completion-date")}>
                <label className={`mb-2 block text-sm font-semibold ${colors.textSecondary}`} data-testid={fieldLabelTestId("notice-info-completion-date")}>
                  {t("project.verification-report.notice-info-completion-date")} <span className={colors.textDanger}>*</span>
                </label>
                <CustomDatePicker
                  selected={formData.noticeInfoCompletionDate}
                  onChange={(date) => handleInputChange("noticeInfoCompletionDate", date)}
                  placeholderText={t("project.verification-report.notice-info-completion-date")}
                  dateFormat="yyyy-MM-dd"
                  minDate={new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDate())}
                  filterDate={(date) => {
                    const today = new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDate());
                    return date >= today;
                  }}
                  data-testid={fieldInputTestId("notice-info-completion-date")}
                />
              </div>
            </div>
          </div>

          {/* Section: Verification Files */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className={`${colors.textPrimary} text-xl font-semibold uppercase`}>
                {t("project.verification.verification-project-version")}
                <span className={colors.textDanger}> *</span>
              </h2>
              {formData.verificationFile && (
                <button onClick={handleAddVerificationFile} className={`px-4 py-2 text-sm font-semibold border ${colors.borderOutLineButton} ${colors.textActive} rounded-lg`} data-testid={fieldInputTestId("change-verification-project-version")}>
                  {t("project.verification.change-verification-project-version")}
                </button>
              )}
            </div>

            <div className={`${colors.bgVerifReportFile} ${colors.borderPrimary} border rounded-lg p-4`}>
              {formData.verificationFile ? (
                renderVerificationFileTable()
              ) : (
                <>
                  <div className="grid grid-cols-5 gap-4 mb-4 px-4 py-2">
                    <div className={`${colors.textSecondary} text-sm font-medium`}>{t("project.verification-report.version")}</div>
                    <div className={`${colors.textSecondary} text-sm font-medium`}>{t("project.verification.verification-file-name")}</div>
                    <div className={`${colors.textSecondary} text-sm font-medium`}>{t("project.verification.verification-status")}</div>
                    <div className={`${colors.textSecondary} text-sm font-medium`}>{t("common.requester")}</div>
                    <div className={`${colors.textSecondary} text-sm font-medium`}>{t("project.project-name")}</div>
                  </div>
                  <div className="my-8 flex flex-col items-center justify-center space-y-4">
                    <div className="flex items-center justify-center">
                      <DropIcon size={100} className={`${colors.textActive}`}/>
                    </div>
                    <p className={`${colors.textSecondary} text-sm`}>
                      {t("project.verification.no-verification-project-version-information")}
                    </p>
                  </div>
                  <div className="mt-4 flex justify-center">
                    <button
                      onClick={handleAddVerificationFile}
                      className={`px-6 py-2 text-sm font-semibold ${colors.buttonBackgroundGradient} ${colors.buttonBackgroundGradientHover} ${colors.textButton} rounded-lg`}
                      data-testid={fieldInputTestId("add-verification-project-version")}
                    >
                      {t("project.verification.add-verification-project-version")}
                    </button>
                  </div>
                </>
              )}
            </div>

            <AddVerificationFileModal
              isOpen={showAddFileModal}
              onClose={handleFileModalClose}
              onAdd={handleFileModalAdd}
              initialSelectedFile={formData.verificationFile || null}
            />
          </div>

          {/* Section: OSS Usage Information */}
          <div>
            <h2 className={` mb-6 ${colors.textPrimary} text-xl font-semibold uppercase`}>{t("project.verification-report.ossused-info")}</h2>
            <ComponentTable
              data={scanComponentData}
              licensePolicyData={licensePolicyData}
              seq={scanComponentKey}
              validationMode="export"
              allowVexReview={true}
              readOnlyVexReview={true}
              vexContext={vexContext}
            />
          </div>
        </div>
      </div>
    </div>
  );
});

export default AddVerificationReportForm;

